import type { AdminEmployee } from '../types/models';

export const BENEFIT_RULE_EFFECTIVE_FROM = '2026-05-16';
// 非常勤の支援金・特別手当を「パート特別手当」1本にする期間の開始日
export const PART_SPECIAL_ALLOWANCE_EFFECTIVE_FROM = '2026-09-16';
export const PART_SPECIAL_ALLOWANCE_LABEL = 'パート特別手当';

export interface PayrollBenefitRuleOptions {
  benefitRuleEnabled?: boolean;
  partSpecialAllowanceEnabled?: boolean;
}

export interface PayrollBenefitLabels {
  benefitLabel: string;
  allowanceLabel: string;
}

const SUPPORT_RATES_BY_EMPLOYEE_ID: Record<string, number> = {
  '18': 70, // 五十幡
  '21': 70, // 関口あさ美
  '15': 70, // 原野
  '19': 70, // 小池
  '40': 70, // 下村
  '39': 70, // 岡本
  '54': 70, // 早川
  '34': 50, // 丹野
  '35': 50, // 関口叶恵
  '41': 50, // 滝澤
  '56': 50, // 竹内
};

const ALLOWANCE_FIXED_RATES_BY_EMPLOYEE_ID: Record<string, number> = {
  '39': 130, // 岡本
  '40': 130, // 下村
  '54': 130, // 早川
  '34': 90,  // 丹野
  '35': 60,  // 関口叶恵
  '41': 60,  // 滝澤
  '56': 100, // 竹内
};

const ALLOWANCE_MERGED_RATES_BY_EMPLOYEE_ID: Record<string, number> = {
  '17': 210, // 荻野
  '21': 120, // 関口あさ美
  '15': 190, // 原野
  '18': 150, // 五十幡
  '19': 200, // 小池
  '20': 500, // 浩平
};

export function getPayrollPeriodStart(selectedMonth: string, closingDay: string): string | null {
  const [year, month] = selectedMonth.split('-').map(Number);
  if (!year || !month) return null;

  if (closingDay === 'month-end') {
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  const closing = Number(closingDay);
  if (!Number.isFinite(closing) || closing < 1) return null;

  const prevMonthDate = new Date(year, month - 2, 1);
  const prevYear = prevMonthDate.getFullYear();
  const prevMonth = prevMonthDate.getMonth() + 1;
  const lastDayOfPrevMonth = new Date(prevYear, prevMonth, 0).getDate();
  const startDay = Math.min(closing + 1, lastDayOfPrevMonth);

  return `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`;
}

export function isBenefitRuleEnabledForPeriod(selectedMonth: string, closingDay: string): boolean {
  const periodStart = getPayrollPeriodStart(selectedMonth, closingDay);
  return Boolean(periodStart && periodStart >= BENEFIT_RULE_EFFECTIVE_FROM);
}

export function isPartSpecialAllowanceEnabledForPeriod(selectedMonth: string, closingDay: string): boolean {
  const periodStart = getPayrollPeriodStart(selectedMonth, closingDay);
  return Boolean(periodStart && periodStart >= PART_SPECIAL_ALLOWANCE_EFFECTIVE_FROM);
}

export function getBenefitLabels(enabled: boolean): PayrollBenefitLabels {
  return enabled
    ? { benefitLabel: '支援金', allowanceLabel: '特別手当' }
    : { benefitLabel: '特別給付金', allowanceLabel: '特別手当' };
}

/**
 * パート特別手当の対象時間。月の勤務時間合計を、29分以下は切り捨て、30分以上は切り上げて時間単位にする。
 */
export function roundPartSpecialAllowanceHours(totalWorkMinutes: number): number {
  if (!Number.isFinite(totalWorkMinutes) || totalWorkMinutes <= 0) return 0;
  const wholeMinutes = Math.floor(totalWorkMinutes);
  const hours = Math.floor(wholeMinutes / 60);
  return wholeMinutes % 60 >= 30 ? hours + 1 : hours;
}

export function getPartSpecialAllowanceUnitPrice(employee: AdminEmployee): number | null {
  const unitPrice = employee.partSpecialAllowanceUnitPrice;
  return unitPrice === null || unitPrice === undefined || !Number.isFinite(unitPrice) ? null : unitPrice;
}

export function calculatePartSpecialAllowance(
  employee: AdminEmployee,
  totalWorkMinutes: number
): { hours: number; unitPrice: number | null; amount: number } {
  const hours = roundPartSpecialAllowanceHours(totalWorkMinutes);
  const unitPrice = getPartSpecialAllowanceUnitPrice(employee);
  return { hours, unitPrice, amount: hours * (unitPrice ?? 0) };
}

export function resolveFulltimeBenefitAmounts(
  employee: AdminEmployee,
  options?: PayrollBenefitRuleOptions
): { specialBenefit: number; specialAllowance: number } {
  const currentBenefit = employee.specialBenefitUnitPrice ?? 0;
  const currentAllowance = employee.specialAllowanceUnitPrice ?? 0;

  if (!options?.benefitRuleEnabled) {
    return { specialBenefit: currentBenefit, specialAllowance: currentAllowance };
  }

  return { specialBenefit: 0, specialAllowance: currentBenefit + currentAllowance };
}

export function resolveParttimeBenefitUnitPrices(
  employee: AdminEmployee,
  options?: PayrollBenefitRuleOptions
): { specialBenefitUnitPrice: number; specialAllowanceUnitPrice: number } {
  const currentBenefit = employee.specialBenefitUnitPrice ?? 0;
  const currentAllowance = employee.specialAllowanceUnitPrice ?? 0;

  if (!options?.benefitRuleEnabled) {
    return {
      specialBenefitUnitPrice: currentBenefit,
      specialAllowanceUnitPrice: currentAllowance,
    };
  }

  const supportRate = SUPPORT_RATES_BY_EMPLOYEE_ID[employee.id] ?? 0;
  const fixedAllowanceRate = ALLOWANCE_FIXED_RATES_BY_EMPLOYEE_ID[employee.id];
  const mergedAllowanceRate = ALLOWANCE_MERGED_RATES_BY_EMPLOYEE_ID[employee.id];
  const allowanceRate = fixedAllowanceRate ?? mergedAllowanceRate ?? currentAllowance;

  return {
    specialBenefitUnitPrice: supportRate,
    specialAllowanceUnitPrice: allowanceRate,
  };
}
